import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Wallet, AlertTriangle, Plus, Loader2 } from "lucide-react";
import { formatUSD, formatDate } from "@/lib/utils";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Master Wallet Balance card.
 *
 * Sits below the KPI grid on the Dashboard. Shows the actual available
 * balance of the platform's Master Wallet (the wallet used to fund the
 * whole network). Only MASTER_ADMIN sees the "Add Funds" action.
 *
 * The balance auto-updates: (a) the parent Dashboard polls every 30s and
 * (b) after a successful Add Funds we call onRefresh() so the number ticks
 * up immediately. Transfers Admin→MasterAgent decrease this balance
 * (through /api/transfers), while transfers MasterAgent→Agent don't touch
 * it — that's already the behaviour of the wallet/ledger backend.
 */
export default function WalletSummaryCard({
  balanceUsd,
  lastUpdated,
  threshold,
  canFund,
  onRefresh,
  delegatedLabel,
  testId = "wallet-summary-card",
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const belowThreshold =
    threshold != null && balanceUsd != null && Number(balanceUsd) < Number(threshold);

  return (
    <>
      <div
        data-testid={testId}
        className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4 p-5">
          {/* Icon block */}
          <div className="w-14 h-14 rounded-md bg-gradient-to-br from-[#0F62FE] to-[#0043CE] flex items-center justify-center flex-shrink-0">
            <Wallet className="w-7 h-7 text-white" strokeWidth={2} />
          </div>

          {/* Labels + balance */}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              {delegatedLabel || t("dashboard.master_wallet_balance")}
            </div>
            <div className="flex items-baseline gap-2 mt-1 flex-wrap">
              <div
                data-testid="wallet-summary-balance"
                className="text-3xl sm:text-4xl font-bold text-slate-900 leading-none"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {balanceUsd == null ? "—" : formatUSD(balanceUsd)}
              </div>
              <span className="text-sm text-slate-500 font-semibold">USD</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {t("dashboard.master_wallet_hint")} · {t("dashboard.last_updated")}{" "}
              {lastUpdated ? formatDate(lastUpdated) : "—"}
            </div>
          </div>

          {/* Action button (admin only) */}
          {canFund && (
            <button
              data-testid="wallet-summary-add-funds"
              onClick={() => setOpen(true)}
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6] transition-colors flex-shrink-0"
            >
              <Plus className="w-4 h-4" /> {t("dashboard.add_funds")}
            </button>
          )}
        </div>

        {/* Low-balance warning strip — full-width, only visible when triggered */}
        {belowThreshold && (
          <div
            data-testid="wallet-summary-low-warning"
            className="flex items-center gap-2 bg-amber-50 border-t border-amber-200 px-5 py-2.5 text-xs font-semibold text-amber-800"
          >
            <AlertTriangle className="w-4 h-4" />
            {t("dashboard.low_balance_warning", { threshold: formatUSD(threshold) })}
          </div>
        )}
      </div>

      {open && (
        <AddFundsModal
          onClose={() => setOpen(false)}
          onSuccess={() => {
            setOpen(false);
            onRefresh?.();
          }}
        />
      )}
    </>
  );
}

function AddFundsModal({ onClose, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      setErr("Amount must be greater than 0");
      return;
    }
    if (amt > 1_000_000) {
      setErr("Amount is too large");
      return;
    }
    setBusy(true);
    try {
      const me = await api.get("/auth/me");
      await api.post("/payments/admin/manual-funding", {
        user_id: me.data.id,
        amount_usd: amt,
        note: note.trim() || `Manual funding via Dashboard`,
      });
      toast.success(
        `Added ${amt.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })} to your Master Wallet`
      );
      onSuccess?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Funding failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/50"
      onClick={onClose}
      data-testid="add-funds-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4"
      >
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            Manual funding
          </div>
          <h3
            className="text-xl font-bold text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Add funds to your Master Wallet
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Adds a credit to your Master Admin wallet and records it in the ledger as{" "}
            <code className="text-[10px] bg-slate-100 px-1 rounded">FUNDING_MANUAL</code>. The
            new balance will be reflected on the dashboard immediately.
          </p>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
            Amount (USD)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">
              $
            </span>
            <input
              data-testid="add-funds-amount"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              autoFocus
              className="flex h-11 w-full rounded-lg border border-slate-300 bg-white pl-7 pr-3 py-2 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              placeholder="500.00"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
            Note (optional)
          </label>
          <input
            data-testid="add-funds-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            className="flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            placeholder="e.g. Cash deposit from BOD account"
          />
        </div>

        {err && (
          <div
            data-testid="add-funds-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg"
          >
            {err}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-11 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="add-funds-submit"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Add funds
          </button>
        </div>
      </form>
    </div>
  );
}
